import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { Plus, Car, Search } from "lucide-react";
import WarrantyStatusBadge from "../components/WarrantyStatusBadge.jsx";
import NewVehicleRegistrationDialog from "../components/NewVehicleRegistrationDialog.jsx";
import { fmtDate } from "../lib/format.js";

export default function VehicleRegistrationsPage() {
  const navigate = useNavigate();
  const [vrs, setVrs] = useState([]);
  const [enums, setEnums] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ brand: "", branch: "", warranty_status: "" });
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.append("q", q.trim());
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await api.get(`/vehicle-registrations?${params}`);
      setVrs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/enums").then((r) => setEnums(r.data));
  }, []);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [q, JSON.stringify(filters)]);

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Car className="w-6 h-6 text-[#1F3864]" strokeWidth={1.8} />
            Registo de Viaturas
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            {loading ? "…" : `${vrs.length} viatura${vrs.length === 1 ? "" : "s"} registada${vrs.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/customers")}
            className="text-sm border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-2"
            data-testid="vr-goto-customers-btn"
          >
            Clientes
          </button>
          <button
            data-testid="vr-new-btn"
            onClick={() => setShowNew(true)}
            className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-2 text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Registar viatura
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block col-span-1 md:col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Pesquisar</span>
          <div className="relative mt-0.5">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Matrícula, VIN, cliente, telefone…"
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="vr-search-input"
            />
            <Search className="w-4 h-4 text-[#6C757D] absolute left-2 top-1/2 -translate-y-1/2" />
          </div>
        </label>
        <Select label="Marca" value={filters.brand} onChange={(v) => setFilters({ ...filters, brand: v })} options={[{ value: "", label: "Todas" }, ...(enums?.brands || []).map((b) => ({ value: b, label: b }))]} />
        <Select label="Dealer" value={filters.branch} onChange={(v) => setFilters({ ...filters, branch: v })} options={[{ value: "", label: "Todos" }, ...(enums?.branches || []).map((b) => ({ value: b, label: b }))]} />
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
            <tr>
              <Th>VR</Th><Th>Matrícula</Th><Th>Marca / Modelo</Th><Th>Cliente</Th><Th>Telefone</Th>
              <Th>Data venda</Th><Th>Garantia</Th><Th>Dealer</Th><Th>Vendedor</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6C757D]">A carregar…</td></tr>
            ) : vrs.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6C757D]" data-testid="vr-empty-state">Sem viaturas registadas</td></tr>
            ) : vrs.map((v) => (
              <tr
                key={v.id}
                onClick={() => navigate(`/vehicle-registrations/${v.id}`)}
                data-testid={`vr-row-${v.id}`}
                className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer"
              >
                <Td mono>{v.vr_number}</Td>
                <Td mono className="text-[#1F3864] font-semibold">{v.registration_plate}</Td>
                <Td>{v.brand} {v.model} · <span className="text-[#6C757D]">{v.year}</span></Td>
                <Td>{v.customer?.name || "—"}</Td>
                <Td className="text-[#6C757D]">{v.customer?.phone}</Td>
                <Td>{fmtDate(v.sale_date)}</Td>
                <Td><WarrantyStatusBadge status={v.warranty_status} daysToExpiry={v.warranty_days_to_expiry} /></Td>
                <Td>{v.branch}</Td>
                <Td className="text-[#6C757D]">{v.salesperson_name || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && enums && (
        <NewVehicleRegistrationDialog
          enums={enums}
          onClose={() => setShowNew(false)}
          onCreated={(vr) => { setShowNew(false); navigate(`/vehicle-registrations/${vr.id}`); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
